import { Component, Input, OnInit, forwardRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import EpiService, { EpiDoc, SelectedEpiItem } from '../../services/epis.service';

interface EpiRow {
  selectedEpiId: string;
  size: string;
}

@Component({
  selector: 'epi-selector',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './epi-selector.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EpiSelectorComponent),
      multi: true,
    },
  ],
})
export class EpiSelectorComponent implements OnInit, ControlValueAccessor {
  @Input() label: string = '';

  epiList = signal<EpiDoc[]>([]);
  isLoading = signal<boolean>(true);
  disabled = signal<boolean>(false);

  // État interne du composant (liste des lignes éditables)
  rows = signal<EpiRow[]>([{ selectedEpiId: '', size: '' }]);

  // Callbacks de ControlValueAccessor
  private onChange: (value: SelectedEpiItem[]) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private epiService: EpiService) {}

  ngOnInit(): void {
    this.epiService.all().subscribe((data) => {
      this.epiList.set(data);
      this.isLoading.set(false);
    });
  }

  addRow(): void {
    this.rows.update((prev) => [...prev, { selectedEpiId: '', size: '' }]);
    this.onRowChange();
  }

  removeRow(index: number): void {
    if (this.rows().length > 1) {
      this.rows.update((prev) => prev.filter((_, i) => i !== index));
      this.onRowChange();
    }
  }

  onRowChange(): void {
    this.onTouched();

    // Map vers le format final attendu par le FormGroup
    const result: SelectedEpiItem[] = this.rows()
      .map((row) => {
        const foundEpi = this.epiList().find((e) => e._id === row.selectedEpiId);
        if (!foundEpi) return null;

        return {
          _id: foundEpi._id,
          name: foundEpi.name,
          price: foundEpi.price,
          size: row.size,
        };
      })
      .filter((item): item is SelectedEpiItem => item !== null);

    this.onChange(result);
  }

  // --- Implémentation de ControlValueAccessor ---

  writeValue(value: SelectedEpiItem[] | null): void {
    if (value && Array.isArray(value) && value.length > 0) {
      this.rows.set(
        value.map((item) => ({
          selectedEpiId: item._id,
          size: item.size || '',
        })),
      );
    } else {
      this.rows.set([{ selectedEpiId: '', size: '' }]);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
